import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { FiCheckCircle, FiAlertCircle, FiChevronDown, FiGift } from "react-icons/fi";
import {
  RiInstagramLine,
  RiMailLine,
  RiUserLine,
  RiArrowRightLine,
} from "react-icons/ri";
import { useTranslation } from "react-i18next";

import fondo from "../assets/fondo1.jpg";
import videoPresentacion from "../assets/mento.MP4";
import videoPoster from "../assets/mentoria-video-poster.png";

import Spinner from "../layout/Spinner";
import RuletaDescuento, { getPremioActivo } from "./RuletaDescuento";

// ─── CHECK GROUP OPTIONS ──────────────────────────────────────────────────────
const FRENOS = [
  "No encontré un estilo claro",
  "No sé comunicar mi trabajo",
  "Me cuesta cobrar más",
  "Tengo clientes pero no alineados",
  "No sé posicionarme",
  "Quiero construir algo más grande que tatuar",
];

// ─── MÉTODO A.L.M.A ───────────────────────────────────────────────────────────
const ALMA = [
  {
    letra: "A",
    titulo: "AUTENTICIDAD",
    intro:
      "Si sentís que todavía dependés demasiado de referencias externas o no encontraste algo que haga reconocible tu trabajo, este espacio es para construir una identidad propia.",
    items: [
      "Cómo desarrollar un estilo reconocible",
      "Qué hace distinto tu trabajo",
      "Cómo dejar de depender de tendencias",
      "Construcción de una visión artística",
      "Sensibilidad como diferencial",
    ],
  },
  {
    letra: "L",
    titulo: "LENGUAJE",
    intro:
      "Muchas veces el problema no es tu trabajo. Es que todavía no aprendiste a mostrarlo de una forma que las personas entiendan el valor que tiene.",
    items: [
      "Cómo comunicar profundidad",
      "Storytelling",
      "Contenido",
      "Marca personal",
      "Mostrarte sin sentirte falso",
    ],
  },
  {
    letra: "M",
    titulo: "MARCA",
    intro:
      "Hay artistas increíbles que siguen compitiendo por precio porque nunca construyeron una percepción distinta alrededor de su trabajo.",
    items: [
      "Posicionamiento",
      "Experiencia",
      "Valor",
      "Comunidad",
      "Clientes alineados",
    ],
  },
  {
    letra: "A",
    titulo: "AUTORIDAD",
    intro:
      "Tu carrera no tiene por qué depender únicamente de tatuar más horas. Existen otras formas de construir oportunidades desde lo que hacés.",
    items: [
      "Cómo construir una carrera que no dependa únicamente de tatuar más horas",
      "Viajes",
      "Colaboraciones",
      "Nuevas fuentes de ingreso",
      "Posicionamiento",
      "Expansión",
    ],
  },
];

// ─── ANIMACIONES ──────────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -24 },
  show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

// ─── ACORDEÓN ITEM ────────────────────────────────────────────────────────────
const AlmaItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      variants={fadeUp}
      className="border-t border-[#3C2B22]/60 last:border-b last:border-[#3C2B22]/60"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-5 gap-6 group text-left"
      >
        <div className="flex items-center gap-5">
          <span className="text-[#3C2B22]/80 text-xs tracking-[0.35em] font-light w-4">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-[#B8977F] text-xs tracking-[0.4em] uppercase font-medium">
            {item.titulo}
          </span>
        </div>
        <FiChevronDown
          className={`text-[#7A5E50] flex-shrink-0 transition-transform duration-400 ${
            open ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-7 pl-9 pr-4 space-y-5">
              <p className="text-[#D4BCAA] text-sm font-light italic leading-relaxed">
                {item.intro}
              </p>
              <p className="text-[#7A5E50] text-[10px] tracking-[0.3em] uppercase">
                Dentro de este pilar exploraremos:
              </p>
              <ul className="space-y-2">
                {item.items.map((punto, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-[7px] flex-shrink-0 w-[3px] h-[3px] rounded-full bg-[#B8977F]" />
                    <span className="text-[#B8977F] text-sm font-light italic leading-snug">
                      {punto}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── CHECK GROUP ──────────────────────────────────────────────────────────────
const CheckGroup = ({ label, group, options, selected, onToggle }) => (
  <motion.div variants={fadeUp} className="sm:col-span-2 flex flex-col gap-3">
    <label className="text-xs text-[#7A5E50] uppercase tracking-[0.3em]">
      {label}
    </label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(group, opt)}
            className={`
              flex items-center gap-3 px-4 py-3 text-left
              border transition-all duration-300 cursor-pointer
              ${
                checked
                  ? "border-[#B8977F]/70 bg-[#B8977F]/10"
                  : "border-[#3C2B22]/60 bg-[#1C1410]/80 hover:border-[#7A5E50]/60"
              }
            `}
          >
            <span
              className={`
                flex-shrink-0 w-4 h-4 border flex items-center justify-center
                transition-all duration-300
                ${
                  checked
                    ? "border-[#B8977F] bg-[#B8977F]"
                    : "border-[#3C2B22] bg-transparent"
                }
              `}
            >
              {checked && (
                <motion.svg
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  width="9"
                  height="7"
                  viewBox="0 0 9 7"
                  fill="none"
                >
                  <path
                    d="M1 3L3.5 5.5L8 1"
                    stroke="#0E0B09"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              )}
            </span>
            <span
              className={`text-xs font-light leading-snug transition-colors duration-300 ${
                checked
                  ? "text-[#D4BCAA]"
                  : "text-[#7A5E50] hover:text-[#B8977F]"
              }`}
            >
              {opt}
            </span>
          </button>
        );
      })}
    </div>
  </motion.div>
);

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const Mentorias = () => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    nombre: "",
    instagram: "",
    email: "",
    frenos: [],
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [premio, setPremio] = useState(() => getPremioActivo());

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleCheck = (group, value) => {
    setForm((prev) => {
      const current = prev[group];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [group]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.nombre || !form.email) {
      setStatus({ type: "error", message: t("mentorships.alerts.required") });
      return;
    }

    setLoading(true);

    const premioActivo = getPremioActivo();
    const objetivo = form.frenos.length
      ? `Frenos: ${form.frenos.join(" / ")}`
      : "";

    const payload = {
      nombre: form.nombre,
      apellido: form.instagram,
      email: form.email,
      telefono: "",
      objetivo,
      codigo_descuento: premioActivo ? premioActivo.code : "",
      premio: premioActivo ? premioActivo.label : "",
    };

    try {
      const res = await axios.post(
        "https://www.aldanaselena.com/mentorias.php",
        payload,
        { headers: { "Content-Type": "application/json" }, timeout: 15000 }
      );

      if (res.data?.success === true || res.data?.success === "true") {
        setStatus({ type: "success", message: t("mentorships.alerts.success") });
        setForm({ nombre: "", instagram: "", email: "", frenos: [] });
      } else {
        setStatus({
          type: "error",
          message: res.data?.error || t("mentorships.alerts.generic"),
        });
      }
    } catch (err) {
      if (err.response) {
        setStatus({
          type: "error",
          message: err.response.data?.error || t("mentorships.alerts.server"),
        });
      } else if (err.request) {
        setStatus({ type: "error", message: t("mentorships.alerts.connection") });
      } else {
        setStatus({ type: "error", message: t("mentorships.alerts.unexpected") });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-montserrat">

      {/* BACKGROUND */}
      <img
        src={fondo}
        alt="fondo"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#0E0B09]/80 backdrop-blur-sm" />

      <div className="relative z-10 px-6 md:px-12 lg:px-20 py-28 max-w-7xl mx-auto">

        {/* ── 1. HEADER ──────────────────────────────────────────────────────── */}
        <motion.div
          className="mb-16"
          initial="hidden"
          animate="show"
          variants={stagger}
        >
          <motion.p
            variants={fadeLeft}
            className="text-[#7A5E50] text-xs tracking-[0.4em] uppercase mb-4"
          >
            Mentoría individual
          </motion.p>

          <motion.h1
            variants={fadeLeft}
            className="text-3xl md:text-5xl lg:text-6xl uppercase font-light tracking-[0.2em] text-[#B8977F] leading-tight"
          >
            Mentorías
            <br />
            <span className="text-[#EDE0D0]">para artistas</span>
          </motion.h1>

          <motion.p
            variants={fadeLeft}
            className="mt-5 text-[#7A5E50] text-xs md:text-sm tracking-[0.25em] uppercase font-light"
          >
            Encontrá tu estilo&nbsp;·&nbsp;Aprendé a comunicarlo&nbsp;·&nbsp;Posicionate&nbsp;·&nbsp;Viví de tu arte
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-8 w-full max-w-5xl h-[1px] bg-[#3C2B22]/60"
          />
        </motion.div>

        {/* 2. VIDEO PRESENTACION */}
        <motion.div
          className="mb-24 max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <video
            src={videoPresentacion}
            poster={videoPoster}
            controls
            playsInline
            preload="none"
            className="w-full border border-[#3C2B22]/60 shadow-2xl"
          />
          <p className="mt-4 text-[#7A5E50] text-[10px] tracking-[0.3em] uppercase text-center">
            Mirá de qué se trata la mentoría
          </p>
        </motion.div>

        {/* ── 3. SECCIÓN DOLORES ─────────────────────────────────────────────── */}
        <motion.div
          className="mb-24 max-w-2xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.p
            variants={fadeLeft}
            className="text-[#D4BCAA] text-base md:text-lg font-light italic mb-10 leading-relaxed"
          >
            ¿Sentís que alguna de estas cosas
            <br />
            <strong className="text-[#EDE0D0] not-italic">te está frenando?</strong>
          </motion.p>

          <div className="space-y-8">
            {[
              {
                bold: "No encontrás el estilo",
                rest: "que realmente te representa.",
              },
              {
                bold: "Cobrás menos",
                rest: "de lo que querés.",
              },
              {
                bold: "No sabés mostrar tu trabajo",
                rest: "para que las personas entiendan lo que vale.",
              },
              {
                bold: "No atraés clientes",
                rest: "alineados con tu visión.",
              },
              {
                bold: "Sentís que tu trabajo tiene potencial",
                rest: "pero todavía no lográs diferenciarte.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeLeft}
                className="flex items-start gap-5"
              >
                <span className="mt-1 flex-shrink-0 text-[#3C2B22]/80 text-xs tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-[#B8977F] font-light leading-snug">
                  <strong className="block text-base md:text-lg text-[#D4BCAA] not-italic font-normal">
                    {item.bold}
                  </strong>
                  <span className="text-sm md:text-base italic">{item.rest}</span>
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ── 4. MÉTODO A.L.M.A ─────────────────────────────────────────────── */}
        <motion.div
          className="mb-24 max-w-2xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
        >
          {/* encabezado del método */}
          <motion.div variants={fadeUp} className="mb-2">
            <p className="text-[#7A5E50] text-xs tracking-[0.4em] uppercase mb-3">
              El proceso
            </p>
            <h2 className="text-2xl md:text-3xl uppercase font-light tracking-[0.3em] text-[#B8977F]">
              Método A.L.M.A
            </h2>
            <p className="mt-2 text-[#7A5E50] text-xs tracking-[0.2em] uppercase font-light">
              Encontrá tu estilo&nbsp;·&nbsp;Aprendé a comunicarlo&nbsp;·&nbsp;Posicionate&nbsp;·&nbsp;Viví de tu arte
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8">
            {ALMA.map((item, i) => (
              <AlmaItem key={i} item={item} index={i} />
            ))}
          </motion.div>
        </motion.div>

        {/* ── 5. ¿POR QUÉ TRABAJO ESTAS COSAS? ─────────────────────────────── */}
        <motion.div
          className="mb-24 max-w-xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-8">
            <div className="h-[1px] w-8 bg-[#3C2B22]/60" />
            <p className="text-[#7A5E50] text-xs tracking-[0.35em] uppercase">
              Por qué trabajo estas cosas
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-5">
            <p className="text-[#D4BCAA] text-sm md:text-base font-light italic leading-relaxed">
              Durante años pensé que crecer como artista dependía únicamente de{" "}
              <strong className="text-[#EDE0D0] not-italic">mejorar la técnica.</strong>
            </p>
            <p className="text-[#B8977F] text-sm md:text-base font-light italic leading-relaxed">
              Pero mi carrera también se construyó explorando imagen, fotografía,
              comunicación, posicionamiento y formas de transmitir una visión.
            </p>
            <p className="text-[#D4BCAA] text-sm md:text-base font-light italic leading-relaxed">
              Entender eso cambió{" "}
              <strong className="text-[#EDE0D0] not-italic">
                cómo me mostraba, cuánto cobraba
              </strong>{" "}
              y las oportunidades que aparecieron.
            </p>
            <p className="text-[#B8977F] text-sm md:text-base font-light italic leading-relaxed">
              Hoy gran parte de lo que comparto nace de ese recorrido.
            </p>
          </motion.div>
        </motion.div>

        {/* ── DIVIDER ────────────────────────────────────────────────────────── */}
        <motion.div
          className="flex items-center gap-6 mb-20"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <div className="flex-1 h-[1px] bg-[#3C2B22]/50" />
          <p className="text-[#7A5E50] text-xs tracking-[0.35em] uppercase whitespace-nowrap">
            Acceso limitado · Aplicación previa requerida
          </p>
          <div className="flex-1 h-[1px] bg-[#3C2B22]/50" />
        </motion.div>

        {/* ── 6. INFO + FORM ─────────────────────────────────────────────────── */}
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-16 items-start">

          {/* INFO */}
          <motion.div
            className="flex flex-col gap-8 lg:sticky lg:top-32"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <p className="text-[#7A5E50] text-xs tracking-[0.3em] uppercase">
                {t("mentorships.info1")}
              </p>
              <p className="mt-2 text-[#B8977F] text-sm italic font-light">
                Acceso limitado
              </p>
            </motion.div>

            <motion.div variants={fadeUp}>
              <p className="text-[#7A5E50] text-xs tracking-[0.3em] uppercase">
                {t("mentorships.info2")}
              </p>
              <div className="mt-3 max-w-md">
                <p className="text-[#D4BCAA] text-sm md:text-base italic font-light leading-relaxed">
                  {t("mentorships.priority")}
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="border-l-2 border-[#3C2B22]/60 pl-5"
            >
              <p className="text-[#7A5E50] text-xs italic font-light leading-relaxed">
                "No esperes tener todo listo.
                <br />
                El momento es ahora."
              </p>
            </motion.div>
          </motion.div>

          {/* FORM */}
          <motion.form
            id="aplicacion"
            onSubmit={handleSubmit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
          >
            {/* NOMBRE */}
            <motion.div variants={fadeUp} className="flex flex-col">
              <label className="text-xs mb-2 text-[#7A5E50] uppercase tracking-wider">
                {t("mentorships.form.name")}
              </label>
              <div className="relative">
                <RiUserLine className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5E50] text-base pointer-events-none" />
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  type="text"
                  autoComplete="off"
                  className="w-full bg-[#1C1410]/80 border border-[#3C2B22]/60 pl-10 pr-4 py-3 text-[#EDE0D0] outline-none focus:border-[#B8977F] transition text-sm"
                />
              </div>
            </motion.div>

            {/* INSTAGRAM */}
            <motion.div variants={fadeUp} className="flex flex-col">
              <label className="text-xs mb-2 text-[#7A5E50] uppercase tracking-wider">
                @ de Instagram
              </label>
              <div className="relative">
                <RiInstagramLine className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5E50] text-base pointer-events-none" />
                <input
                  name="instagram"
                  value={form.instagram}
                  onChange={handleChange}
                  type="text"
                  autoComplete="off"
                  placeholder="@tuusuario"
                  className="w-full bg-[#1C1410]/80 border border-[#3C2B22]/60 pl-10 pr-4 py-3 text-[#EDE0D0] outline-none focus:border-[#B8977F] transition text-sm placeholder:text-[#7A5E50]/50"
                />
              </div>
            </motion.div>

            {/* EMAIL */}
            <motion.div variants={fadeUp} className="flex flex-col sm:col-span-2">
              <label className="text-xs mb-2 text-[#7A5E50] uppercase tracking-wider">
                {t("mentorships.form.email")}
              </label>
              <div className="relative">
                <RiMailLine className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5E50] text-base pointer-events-none" />
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  autoComplete="off"
                  className="w-full bg-[#1C1410]/80 border border-[#3C2B22]/60 pl-10 pr-4 py-3 text-[#EDE0D0] outline-none focus:border-[#B8977F] transition text-sm"
                />
              </div>
            </motion.div>

            {/* DIVIDER */}
            <motion.div
              variants={fadeUp}
              className="sm:col-span-2 flex items-center gap-4 py-1"
            >
              <div className="flex-1 h-[1px] bg-[#3C2B22]/40" />
              <p className="text-[#7A5E50] text-[10px] tracking-[0.3em] uppercase">
                Contame más
              </p>
              <div className="flex-1 h-[1px] bg-[#3C2B22]/40" />
            </motion.div>

            {/* CHECK GROUP - FRENOS */}
            <CheckGroup
              label="¿Qué sentís que hoy está frenando más tu crecimiento?"
              group="frenos"
              options={FRENOS}
              selected={form.frenos}
              onToggle={toggleCheck}
            />

            {/* DESCUENTO ACTIVO */}
            {premio && (
              <motion.div
                variants={fadeUp}
                className="sm:col-span-2 flex items-center justify-center gap-3 border border-[#B8977F]/40 bg-[#B8977F]/10 px-4 py-3 text-center"
              >
                <FiGift className="text-[#C6A98A] flex-shrink-0" />
                <p className="text-[#D4BCAA] text-xs tracking-[0.12em] uppercase leading-relaxed">
                  {premio.label} activo con el código {premio.code} — se adjunta
                  solo al enviar tu aplicación
                </p>
              </motion.div>
            )}

            {/* BUTTON */}
            <motion.div variants={fadeUp} className="sm:col-span-2 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="group w-full py-4 bg-[#B8977F] text-[#0E0B09] uppercase tracking-[0.3em] text-sm hover:bg-transparent hover:text-[#EDE0D0] border border-[#B8977F] transition duration-500 flex items-center justify-center gap-3 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Spinner />
                    {t("mentorships.form.sending")}
                  </>
                ) : (
                  <>
                    {t("mentorships.form.submit")}
                    <RiArrowRightLine className="text-base transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </motion.div>

            {/* STATUS */}
            <AnimatePresence mode="wait">
              {status.message && (
                <motion.div
                  key={status.message}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className={`sm:col-span-2 flex items-center justify-center gap-3 text-sm mt-2 px-4 py-4 border ${
                    status.type === "success"
                      ? "border-[#B8977F]/40 bg-[#1C1410]/70 text-[#D4BCAA]"
                      : "border-red-500/30 bg-red-500/10 text-red-300"
                  }`}
                >
                  {status.type === "success" ? (
                    <FiCheckCircle className="text-xl flex-shrink-0" />
                  ) : (
                    <FiAlertCircle className="text-xl flex-shrink-0" />
                  )}
                  <p className="leading-relaxed text-center">{status.message}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.form>
        </div>
      </div>

      {/* RULETA DE DESCUENTO */}
      <RuletaDescuento onPremio={setPremio} />
    </div>
  );
};

export default Mentorias;